import React, { useState, useEffect } from 'react';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Subtitle1Bold, Body2Regular, CaptionLight } from '../../lib/font';
import ColorSquareButton from '../../components/Button/ColorSquareButton';
import { useReservationRules, useUpdateReservationRules } from '@/features/reservationRules/hooks';
import { ReservationRules } from '@/features/reservationRules/types';

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const Toggle = ({ value, onChange, disabled }: { value: boolean; onChange: () => void; disabled?: boolean }) => (
    <button
        onClick={onChange}
        disabled={disabled}
        style={{
            width: 48,
            height: 26,
            borderRadius: 13,
            backgroundColor: value ? '#3B82F6' : '#D1D5DB',
            border: 'none',
            cursor: disabled ? 'default' : 'pointer',
            position: 'relative',
            transition: 'background-color 0.2s',
            flexShrink: 0,
        }}
    >
        <div
            style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: 'white',
                position: 'absolute',
                top: 3,
                left: value ? 25 : 3,
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
        />
    </button>
);

const NumberInput = ({
    label,
    value,
    onChange,
    suffix,
    min = 0,
    step = 0.5,
    integer = false,
    description,
    disabled = false,
}: {
    label: string;
    value: number;
    onChange: (v: number) => void;
    suffix: string;
    min?: number;
    step?: number;
    // integer=true 면 소수점 입력을 막는다. (예: 예약 오픈은 일 단위 — 14.5일은
    // 백엔드에서 floor(14)로 처리되므로 14일과 동일. 혼동을 막기 위해 정수만 받는다.)
    integer?: boolean;
    description?: React.ReactNode;
    disabled?: boolean;
}) => (
    <Col style={{ padding: '10px 0', gap: 6, opacity: disabled ? 0.55 : 1 }}>
        <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{label}</span>
            <Row style={{ alignItems: 'center', gap: 8 }}>
                <input
                    type="number"
                    value={value}
                    min={min}
                    step={integer ? 1 : step}
                    disabled={disabled}
                    onChange={(e) => {
                        const parsed = parseFloat(e.target.value);
                        if (!isNaN(parsed)) {
                            const v = integer ? Math.floor(parsed) : parsed;
                            onChange(Math.max(min, v));
                        }
                    }}
                    style={{
                        width: 88,
                        padding: '8px 12px',
                        border: '1px solid #E1E4E6',
                        borderRadius: 8,
                        fontSize: 14,
                        textAlign: 'right',
                        backgroundColor: disabled ? '#F3F4F6' : 'white',
                        cursor: disabled ? 'not-allowed' : 'auto',
                    }}
                />
                <span style={{ fontSize: 14, color: '#6B7280', minWidth: 40 }}>{suffix}</span>
            </Row>
        </Row>
        {description && (
            <span style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.5, paddingLeft: 2 }}>
                💡 {description}
            </span>
        )}
    </Col>
);

const formatNumber = (n: number) => {
    // 소수점 제거 (정수면 그대로, 아니면 최대 1자리)
    if (Number.isInteger(n)) return n.toString();
    return (Math.round(n * 10) / 10).toString();
};
const WEEKDAY_KR = ['일', '월', '화', '수', '목', '금', '토'];

// ───────────────────────────────────────────────────────────────────────────
// 주간 일괄 공개 미리보기 (회원 앱 달력 + 오픈 일정)
//   계산은 백엔드 groupSchedule.service 의 공개 로직과 1:1로 맞춘다.
//   · 주 시작 = 월요일 / 오픈요일(openDay)은 0=일~6=토
//   · 어떤 '주'는 그 주의 visibilityWeeks 주 '전' (오픈요일·오픈시간)에 공개된다
//   · 매 오픈요일마다 1주치(가장 먼 주)가 새로 열려 항상 visibilityWeeks 주가 보인다
// ───────────────────────────────────────────────────────────────────────────
const ONE_MIN = 60 * 1000;
const atMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDaysD = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addWeeksD = (d: Date, n: number) => addDaysD(d, n * 7);
const startOfWeekMon = (d: Date) => {
    const x = atMidnight(d);
    const dow = x.getDay(); // 0=일
    return addDaysD(x, dow === 0 ? -6 : 1 - dow);
};
const releaseTimeForWeek = (weekStartMon: Date, openDay: number, openMinutes: number) => {
    const offsetFromMonday = (openDay + 6) % 7; // 월=0..일=6
    return new Date(addDaysD(weekStartMon, offsetFromMonday).getTime() + openMinutes * ONE_MIN);
};
const sameDayD = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const fmtMD = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
const fmtAmPm = (mins: number) => {
    const h = Math.floor(mins / 60), m = mins % 60;
    const ampm = h < 12 ? '오전' : '오후';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${ampm} ${h12}:${String(m).padStart(2, '0')}`;
};

const WeeklyReleasePreview = ({ openDay, openTime, visibilityWeeks }: {
    openDay: number; openTime: number; visibilityWeeks: number;
}) => {
    const N = Math.max(Math.floor(visibilityWeeks) || 1, 1);
    const now = new Date();
    const today = atMidnight(now);
    const thisWeekStart = startOfWeekMon(now);
    const releasedThisWeek = now.getTime() >= releaseTimeForWeek(thisWeekStart, openDay, openTime).getTime();
    const weeksAhead = Math.max(releasedThisWeek ? N : N - 1, 0);
    // 현재 회원 앱에 예약 가능한 마지막 날 (가장 먼 공개 주의 일요일)
    const visibleEnd = addDaysD(addWeeksD(thisWeekStart, weeksAhead), 6);

    // 다가오는 오픈 2건 (이번/다음). 이번 주 공개가 이미 지났으면 '이번 오픈'은 다음 주.
    const open1Week = releasedThisWeek ? addWeeksD(thisWeekStart, 1) : thisWeekStart;
    const open1Date = releaseTimeForWeek(open1Week, openDay, openTime);
    const open1NewStart = addWeeksD(open1Week, N);
    const open2Week = addWeeksD(open1Week, 1);
    const open2Date = releaseTimeForWeek(open2Week, openDay, openTime);
    const open2NewStart = addWeeksD(open2Week, N);

    const weeksToShow = Math.max(N + 3, 5);
    const cells: Date[] = [];
    for (let i = 0; i < weeksToShow * 7; i++) cells.push(addDaysD(thisWeekStart, i));
    const monthLabel = cells[0].getMonth() === cells[cells.length - 1].getMonth()
        ? `${cells[0].getMonth() + 1}월`
        : `${cells[0].getMonth() + 1}월 ~ ${cells[cells.length - 1].getMonth() + 1}월`;

    const cellStyle = (d: Date): React.CSSProperties => {
        const base: React.CSSProperties = {
            width: 30, height: 30, borderRadius: '50%', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 12, margin: '0 auto',
            color: '#D1D5DB', fontWeight: 400, boxSizing: 'border-box',
        };
        if (sameDayD(d, today)) return { ...base, backgroundColor: '#2563EB', color: '#fff', fontWeight: 700 };
        if (sameDayD(d, open1Date)) return { ...base, backgroundColor: '#1E3A8A', color: '#fff', fontWeight: 700 };
        if (sameDayD(d, open2Date)) return { ...base, border: '1.5px solid #1E3A8A', color: '#1E3A8A', fontWeight: 700 };
        if (d.getTime() >= today.getTime() && d.getTime() <= visibleEnd.getTime())
            return { ...base, backgroundColor: '#DBEAFE', color: '#1D4ED8', fontWeight: 500 };
        return base;
    };

    const LegendDot = ({ style, label }: { style: React.CSSProperties; label: string }) => (
        <Row style={{ alignItems: 'center', gap: 6 }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', ...style }} />
            <span style={{ fontSize: 11, color: '#6B7280' }}>{label}</span>
        </Row>
    );

    const OpenLine = ({ filled, date, newStart }: { filled: boolean; date: Date; newStart: Date }) => (
        <Row style={{ alignItems: 'flex-start', gap: 8 }}>
            <div style={{
                width: 10, height: 10, borderRadius: '50%', marginTop: 4, flexShrink: 0,
                backgroundColor: filled ? '#1E3A8A' : 'white',
                border: filled ? 'none' : '1.5px solid #1E3A8A',
            }} />
            <Col style={{ gap: 2 }}>
                <span style={{ fontSize: 13, color: '#111827', fontWeight: 600 }}>
                    {filled ? '이번 오픈' : '다음 오픈'} · {fmtMD(date)}({WEEKDAY_KR[date.getDay()]}) {fmtAmPm(openTime)}
                </span>
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                    {fmtMD(newStart)} ~ {fmtMD(addDaysD(newStart, 6))} 새로 공개
                </span>
            </Col>
        </Row>
    );

    return (
        <Col style={{ marginTop: 8, gap: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#6B7280' }}>회원 앱 미리보기</span>

            {/* 달력 */}
            <Col style={{
                padding: 16, backgroundColor: 'white', borderRadius: 14,
                border: '1px solid #E5E7EB', gap: 10, maxWidth: 320,
            }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#111827', textAlign: 'center' }}>{monthLabel}</span>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', rowGap: 6 }}>
                    {['월', '화', '수', '목', '금', '토', '일'].map((w) => (
                        <span key={w} style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>{w}</span>
                    ))}
                    {cells.map((d, i) => (
                        <div key={i} style={cellStyle(d)}>{d.getDate()}</div>
                    ))}
                </div>
                <Row style={{ flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginTop: 4 }}>
                    <LegendDot style={{ backgroundColor: '#2563EB' }} label="오늘" />
                    <LegendDot style={{ backgroundColor: '#DBEAFE' }} label="예약 가능" />
                    <LegendDot style={{ backgroundColor: '#1E3A8A' }} label="이번 오픈일" />
                    <LegendDot style={{ backgroundColor: 'white', border: '1.5px solid #1E3A8A' }} label="다음 오픈일" />
                </Row>
            </Col>

            {/* 오픈 일정 */}
            <Col style={{
                padding: 16, backgroundColor: '#F9FAFB', borderRadius: 12,
                border: '1px solid #E5E7EB', gap: 12,
            }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>오픈 일정</span>
                <span style={{ fontSize: 12, color: '#1D4ED8', lineHeight: 1.6 }}>
                    📱 지금 회원 앱에는 <b>{fmtMD(visibleEnd)}까지</b> 예약 가능 · 매주 <b>{WEEKDAY_KR[openDay]}요일 {fmtAmPm(openTime)}</b>에 1주치씩 새로 공개돼요
                </span>
                <OpenLine filled date={open1Date} newStart={open1NewStart} />
                <OpenLine filled={false} date={open2Date} newStart={open2NewStart} />
            </Col>
        </Col>
    );
};

// 모드 A (예약 오픈 / 수업별 N일 전 롤링) 미리보기.
//   주간 일괄 공개와 달리 '오픈요일/오픈시간' 개념이 없고, 매일 가장 먼 1일치가 열려
//   회원 앱엔 항상 [오늘 ~ 오늘+N일] 의 슬라이딩 윈도우가 보인다.
const PerSessionPreview = ({ bookingOpenDays }: {
    bookingOpenDays: number;
}) => {
    const days = Math.max(Math.floor(bookingOpenDays), 0);
    const now = new Date();
    const today = atMidnight(now);
    const windowEnd = addDaysD(today, days); // 오늘부터 N일 뒤까지가 곧 노출 창

    const thisWeekStart = startOfWeekMon(now);
    const weeksToShow = Math.max(Math.ceil((days + 7) / 7) + 1, 4);
    const cells: Date[] = [];
    for (let i = 0; i < weeksToShow * 7; i++) cells.push(addDaysD(thisWeekStart, i));
    const monthLabel = cells[0].getMonth() === cells[cells.length - 1].getMonth()
        ? `${cells[0].getMonth() + 1}월`
        : `${cells[0].getMonth() + 1}월 ~ ${cells[cells.length - 1].getMonth() + 1}월`;

    const cellStyle = (d: Date): React.CSSProperties => {
        const base: React.CSSProperties = {
            width: 30, height: 30, borderRadius: '50%', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 12, margin: '0 auto',
            color: '#D1D5DB', fontWeight: 400, boxSizing: 'border-box',
        };
        if (sameDayD(d, today)) return { ...base, backgroundColor: '#2563EB', color: '#fff', fontWeight: 700 };
        if (d.getTime() >= today.getTime() && d.getTime() <= windowEnd.getTime())
            return { ...base, backgroundColor: '#DBEAFE', color: '#1D4ED8', fontWeight: 500 };
        return base;
    };

    return (
        <Col style={{ marginTop: 8, gap: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#6B7280' }}>회원 앱 미리보기</span>
            <Col style={{
                padding: 16, backgroundColor: 'white', borderRadius: 14,
                border: '1px solid #E5E7EB', gap: 10, maxWidth: 320,
            }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#111827', textAlign: 'center' }}>{monthLabel}</span>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', rowGap: 6 }}>
                    {['월', '화', '수', '목', '금', '토', '일'].map((w) => (
                        <span key={w} style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>{w}</span>
                    ))}
                    {cells.map((d, i) => (
                        <div key={i} style={cellStyle(d)}>{d.getDate()}</div>
                    ))}
                </div>
                <Row style={{ gap: 16, justifyContent: 'center', marginTop: 4 }}>
                    <Row style={{ alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#2563EB' }} />
                        <span style={{ fontSize: 11, color: '#6B7280' }}>오늘</span>
                    </Row>
                    <Row style={{ alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#DBEAFE' }} />
                        <span style={{ fontSize: 11, color: '#6B7280' }}>예약 가능</span>
                    </Row>
                </Row>
            </Col>
            <Col style={{
                padding: 16, backgroundColor: '#F9FAFB', borderRadius: 12,
                border: '1px solid #E5E7EB', gap: 6,
            }}>
                <span style={{ fontSize: 12, color: '#1D4ED8', lineHeight: 1.6 }}>
                    📱 지금 회원 앱에는 <b>{fmtMD(windowEnd)}까지</b> 예약 가능해요
                </span>
                <span style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>
                    회원 앱엔 항상 <b>오늘부터 {fmtMD(windowEnd)}까지</b>(오늘 포함)가 보이고 예약돼요. 매일 하루씩 뒤로 밀려서 분량이 유지돼요.
                </span>
            </Col>
        </Col>
    );
};

const GroupSettingsPage = ({ createAlert, embedded = false, onNavigateToTickets }: {
    createAlert: any;
    embedded?: boolean;
    onNavigateToTickets?: () => void;
}) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId = authObject?.centerId;

    const { data: rules, isLoading } = useReservationRules({ centerId });
    const updateMutation = useUpdateReservationRules();

    const [form, setForm] = useState<ReservationRules | null>(null);

    useEffect(() => {
        if (rules) setForm({ ...rules });
    }, [rules]);

    const updateField = <K extends keyof ReservationRules>(key: K, value: ReservationRules[K]) => {
        if (!form) return;
        setForm({ ...form, [key]: value });
    };

    const [showGuide, setShowGuide] = useState(() => localStorage.getItem('guide_groupSettings') !== 'hidden');

    const handleSave = async () => {
        if (!form) return;
        // 서버 응답에 포함된 불필요한 필드 제거 후 전송
        // deprecated 필드 명시 제거:
        // - changeLimitEnabled/changeCountLimit: 수강권의 cancellableCount/sameDayCancelCount로 통일
        // - defaultNoshowFee: salesCommission.noshowCommission + Ticket.noshowFee로 통일
        const {
            _id, centerId: _cid, id, __v,
            changeLimitEnabled, changeCountLimit, defaultNoshowFee,
            ...cleanForm
        } = form as any;
        try {
            await updateMutation.mutateAsync({ centerId, body: cleanForm });
            createAlert('저장 완료', '예약 설정이 저장되었습니다.', false);
        } catch {
            createAlert('저장 실패', '설정 저장에 실패했습니다.', true);
        }
    };

    if (isLoading || !form) {
        return (
            <Col style={{ width: '100%', height: '100%', marginTop: 64, alignItems: 'center', justifyContent: 'center' }}>
                <Body2Regular color={colors.LAVEL_1}>설정을 불러오는 중...</Body2Regular>
            </Col>
        );
    }

    // 분 → 시간/일 변환 헬퍼 (소수점 허용으로 정밀도 유지)
    const minsToHours = (mins: number) => mins / 60;
    const hoursToMins = (hours: number) => Math.round(hours * 60);
    const minsToDays = (mins: number) => mins / 1440;
    const daysToMins = (days: number) => Math.round(days * 1440);
    // 예약 오픈 방식: 오늘부터 N일 뒤까지가 회원 앱에 보이고 예약된다 — 그 끝 날짜를 미리 계산해 안내에 표시
    const bookingOpenDays = Math.max(Math.floor(minsToDays(form.bookingOpenDeadline)), 0);
    const bookingWindowEnd = fmtMD(addDaysD(atMidnight(new Date()), bookingOpenDays));

    const outerStyle = embedded
        ? { width: '100%' }
        : { width: '100%', height: '100%', backgroundColor: '#F8FAFC', marginTop: 64, overflow: 'auto' as const };
    const innerStyle = embedded
        ? { width: '100%', maxWidth: 720 }
        : { width: '100%', maxWidth: 720, margin: '0 auto', padding: '40px 32px 80px' };

    return (
        <Col style={outerStyle}>
            <Col style={innerStyle}>
                <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Col>
                        <Row style={{ alignItems: 'center', gap: 8 }}>
                            <Subtitle1Bold>예약 설정</Subtitle1Bold>
                            {!showGuide && (
                                <button onClick={() => { setShowGuide(true); localStorage.removeItem('guide_groupSettings'); }}
                                    style={{ width: 22, height: 22, borderRadius: '50%', border: '1.5px solid #FDBA74', backgroundColor: '#FFF7ED', color: '#EA580C', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>?</button>
                            )}
                        </Row>
                        <CaptionLight color={colors.LAVEL_1} style={{ marginTop: 4 }}>
                            그룹 수업 예약/취소/폐강 규칙을 설정합니다
                        </CaptionLight>
                    </Col>
                    <ColorSquareButton
                        text="저장"
                        size="small"
                        callBack={handleSave}
                        isDisabled={updateMutation.isLoading}
                    />
                </Row>

                {showGuide && (
                    <Col style={{ marginTop: 16, padding: '18px 22px', backgroundColor: '#FFF7ED', borderRadius: 12, border: '1px solid #FDBA74', position: 'relative' }}>
                        <button onClick={() => { setShowGuide(false); localStorage.setItem('guide_groupSettings', 'hidden'); }}
                            style={{ position: 'absolute', top: 10, right: 14, background: 'none', border: 'none', fontSize: 16, color: '#FDBA74', cursor: 'pointer' }}>✕</button>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#9A3412', marginBottom: 6 }}>예약 설정이란?</span>
                        <Col style={{ gap: 3, fontSize: 13, color: '#9A3412', lineHeight: 1.7 }}>
                            <span>그룹 수업의 <b>예약 오픈, 취소 마감, 폐강, 대기자 자동 확정</b> 등의 규칙을 설정합니다.</span>
                            <span>여기서 설정한 규칙은 모든 수업에 기본으로 적용되며, 수업별로 개별 설정도 가능합니다.</span>
                        </Col>
                    </Col>
                )}

                <Col style={{ marginTop: 32, gap: 20 }}>

                    {/* ── 회원 앱 공개 방식 ── */}
                    <Section title="회원 앱 공개 방식" description="회원 앱에 수업이 언제 공개되고 예약되는지 정합니다. 둘 중 하나의 방식으로 운영돼요">
                        {/* 공개 방식 선택 (택1) */}
                        <Col style={{ gap: 8, padding: '4px 0 2px' }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>공개 방식 선택</span>
                            <Row style={{ gap: 10 }}>
                                {[
                                    { on: false, title: '예약 오픈', sub: '오늘부터 정한 일수까지 보임' },
                                    { on: true, title: '주간 일괄 공개', sub: '매주 요일·시간에 한 주씩' },
                                ].map((opt) => {
                                    const selected = !!form.weeklyReleaseEnabled === opt.on;
                                    return (
                                        <button
                                            key={String(opt.on)}
                                            onClick={() => updateField('weeklyReleaseEnabled', opt.on)}
                                            style={{
                                                flex: 1, textAlign: 'left', padding: '12px 14px', borderRadius: 10,
                                                border: selected ? '2px solid #2563EB' : '1px solid #E1E4E6',
                                                backgroundColor: selected ? '#EFF6FF' : 'white',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <Row style={{ alignItems: 'center', gap: 6 }}>
                                                <span style={{ fontSize: 14, fontWeight: 700, color: selected ? '#1D4ED8' : '#374151' }}>{opt.title}</span>
                                                {selected && (
                                                    <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', backgroundColor: '#2563EB', padding: '2px 7px', borderRadius: 999 }}>현재 적용</span>
                                                )}
                                            </Row>
                                            <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: selected ? '#3B82F6' : '#9CA3AF' }}>{opt.sub}</span>
                                        </button>
                                    );
                                })}
                            </Row>
                        </Col>

                        {/* 현재 운영 상태 */}
                        <div style={{ marginTop: 10, padding: '12px 14px', backgroundColor: '#EFF6FF', borderRadius: 10, border: '1px solid #BFDBFE' }}>
                            <span style={{ fontSize: 13, color: '#1E3A8A', lineHeight: 1.6, fontWeight: 600 }}>
                                {form.weeklyReleaseEnabled
                                    ? <>🔵 지금 <b>주간 일괄 공개</b> 방식으로 운영 중 — 매주 <b>{WEEKDAY_KR[form.visibilityOpenDay] ?? '?'}요일 {String(Math.floor(form.visibilityOpenTime / 60)).padStart(2, '0')}:{String(form.visibilityOpenTime % 60).padStart(2, '0')}</b>에 1주치씩 공개·예약돼요</>
                                    : bookingOpenDays <= 0
                                        ? <>🔵 지금 <b>예약 오픈</b> 방식으로 운영 중 — <b>오늘(당일)</b> 수업까지 회원 앱에 보이고 예약돼요</>
                                        : <>🔵 지금 <b>예약 오픈</b> 방식으로 운영 중 — 오늘부터 <b>{bookingOpenDays}일 뒤({bookingWindowEnd})</b>까지의 수업이 회원 앱에 보이고 예약돼요</>}
                            </span>
                        </div>

                        {form.weeklyReleaseEnabled ? (
                            <>
                                <NumberInput
                                    label="노출 주 수"
                                    value={form.visibilityWeeks}
                                    onChange={(v) => updateField('visibilityWeeks', v)}
                                    suffix="주"
                                    min={1}
                                    integer
                                    description={
                                        <>회원 앱에 현재 주 포함 최대 <b>{form.visibilityWeeks}주</b> 분량의 수업이 보여요 (오픈 시각 전에는 한 주 적게 보일 수 있어요)</>
                                    }
                                />
                                <Row style={{ alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                                    <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>오픈 요일</span>
                                    <Row style={{ gap: 4 }}>
                                        {DAY_LABELS.map((label, idx) => {
                                            const isSelected = form.visibilityOpenDay === idx;
                                            return (
                                                <button
                                                    key={idx}
                                                    onClick={() => updateField('visibilityOpenDay', idx)}
                                                    style={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: 8,
                                                        border: isSelected ? '2px solid #3B82F6' : '1px solid #E1E4E6',
                                                        backgroundColor: isSelected ? '#EFF6FF' : 'white',
                                                        color: isSelected ? '#2563EB' : '#6B7280',
                                                        fontSize: 13,
                                                        fontWeight: isSelected ? 600 : 400,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </Row>
                                </Row>
                                <Row style={{ alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                                    <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>오픈 시간</span>
                                    <input
                                        type="time"
                                        value={`${String(Math.floor(form.visibilityOpenTime / 60)).padStart(2, '0')}:${String(form.visibilityOpenTime % 60).padStart(2, '0')}`}
                                        onChange={(e) => {
                                            const [h, m] = e.target.value.split(':').map(Number);
                                            updateField('visibilityOpenTime', h * 60 + m);
                                        }}
                                        style={{
                                            padding: '8px 12px',
                                            border: '1px solid #E1E4E6',
                                            borderRadius: 8,
                                            fontSize: 14,
                                        }}
                                    />
                                </Row>

                                <span style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>
                                    💡 공개되는 순간 바로 예약할 수 있어요. 모든 회원에게 동시에 열려서 인기 수업도 공평하게 예약돼요.
                                </span>

                                <WeeklyReleasePreview
                                    openDay={form.visibilityOpenDay}
                                    openTime={form.visibilityOpenTime}
                                    visibilityWeeks={form.visibilityWeeks}
                                />
                            </>
                        ) : (
                            <>
                                <NumberInput
                                    label="예약 오픈"
                                    value={minsToDays(form.bookingOpenDeadline)}
                                    onChange={(v) => updateField('bookingOpenDeadline', daysToMins(v))}
                                    suffix="일 전"
                                    integer
                                    description={
                                        bookingOpenDays <= 0
                                            ? '오늘(당일) 수업까지 회원 앱에 보이고 바로 예약할 수 있어요'
                                            : <>오늘부터 <b>{bookingOpenDays}일 뒤({bookingWindowEnd})</b>까지의 수업이 회원 앱에 보이고 바로 예약돼요. 매일 하루씩 뒤로 밀려요</>
                                    }
                                />
                                <span style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>
                                    💡 정해진 요일·시간에 한 주씩 통째로 열고 싶다면 위에서 <b>주간 일괄 공개</b>를 선택하세요.
                                </span>

                                <PerSessionPreview
                                    bookingOpenDays={minsToDays(form.bookingOpenDeadline)}
                                />
                            </>
                        )}
                    </Section>

                    {/* ── 마감 규칙 ── */}
                    <Section title="마감 규칙" description="수업 시작 기준으로 각 마감 시점을 설정합니다 (공개·예약 오픈 시점은 위 ‘회원 앱 공개 방식’에서 설정)">
                        <NumberInput
                            label="취소 마감"
                            value={minsToHours(form.cancellationDeadline)}
                            onChange={(v) => updateField('cancellationDeadline', hoursToMins(v))}
                            suffix="시간 전"
                            description={
                                minsToHours(form.cancellationDeadline) <= 0
                                    ? '늦은 취소 없이, 수업 시작 전에 취소하면 회차가 돌아와요 (회원 본인 취소는 아래 회원 취소 마감까지)'
                                    : <>수업 시작 <b>{formatNumber(minsToHours(form.cancellationDeadline))}시간 전</b>까지 취소하면 회차가 돌아와요. 이후 취소는 늦은 취소로 회차가 차감돼요 (강사·관리자가 패널티 면제로 취소하면 돌려줘요)</>
                            }
                        />
                        <NumberInput
                            label="회원 취소 마감"
                            value={minsToHours(form.reservationChangeDeadline)}
                            onChange={(v) => updateField('reservationChangeDeadline', hoursToMins(v))}
                            suffix="시간 전"
                            description={
                                <>수업 시작 <b>{formatNumber(minsToHours(form.reservationChangeDeadline))}시간 전</b>부터는 회원이 앱에서 직접 취소할 수 없어요. 강사·관리자는 취소할 수 있어요</>
                            }
                        />
                        <NumberInput
                            label="폐강 기준"
                            value={minsToHours(form.closingDeadline)}
                            onChange={(v) => updateField('closingDeadline', hoursToMins(v))}
                            suffix="시간 전"
                            description={
                                <>수업 시작 <b>{formatNumber(minsToHours(form.closingDeadline))}시간 전</b> 시점의 예약 인원으로 폐강 여부를 판정해요</>
                            }
                        />
                        <NumberInput
                            label="대기 자동 확정 마감"
                            value={minsToHours(form.waitlistAutoDeadline)}
                            onChange={(v) => updateField('waitlistAutoDeadline', hoursToMins(v))}
                            suffix="시간 전"
                            description={
                                <>자리가 나면 대기자를 순서대로 자동 확정해요. 수업 시작 <b>{formatNumber(minsToHours(form.waitlistAutoDeadline))}시간 전</b>부터는 자동 확정하지 않아요</>
                            }
                        />
                        <Row style={{ alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                            <Col style={{ gap: 2 }}>
                                <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>자동 폐강</span>
                                <span style={{ fontSize: 12, color: '#6B7280' }}>
                                    {form.autoClosingEnabled
                                        ? '최소 인원 미달 시 폐강 기준 시점에 수업이 자동 취소돼요 (예약자 전원 복구)'
                                        : '자동 폐강이 꺼져있어요. 수업은 자동으로 취소되지 않아요'}
                                </span>
                            </Col>
                            <Toggle
                                value={form.autoClosingEnabled}
                                onChange={() => updateField('autoClosingEnabled', !form.autoClosingEnabled)}
                            />
                        </Row>
                    </Section>

                    {/* ── 노쇼 정책 ── */}
                    <Section title="노쇼 정책" description="무단 결석 시 수강권 차감 및 예약 제한 설정">
                        <Row style={{ alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                            <Col style={{ gap: 2 }}>
                                <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>노쇼 추가 차감</span>
                                <span style={{ fontSize: 12, color: '#6B7280' }}>
                                    {form.noshowEnabled
                                        ? '노쇼 발생 시 수강권에서 지정한 횟수만큼 추가 차감돼요'
                                        : '노쇼 추가 차감이 꺼져있어요. 기본 1회만 차감되고 추가 패널티는 없어요'}
                                </span>
                            </Col>
                            <Toggle
                                value={form.noshowEnabled}
                                onChange={() => updateField('noshowEnabled', !form.noshowEnabled)}
                            />
                        </Row>
                        {form.noshowEnabled && (
                            <NumberInput
                                label="차감 횟수"
                                value={form.noshowDeductCount}
                                onChange={(v) => updateField('noshowDeductCount', v)}
                                suffix="회"
                                min={1}
                                description={
                                    <>노쇼 발생 시 수강권 잔여 횟수에서 <b>{form.noshowDeductCount}회</b>를 추가로 차감해요</>
                                }
                            />
                        )}
                        <Row style={{ alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                            <Col style={{ gap: 2 }}>
                                <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>연속 노쇼 제한</span>
                                <span style={{ fontSize: 12, color: '#6B7280' }}>
                                    {form.consecutiveNoshowEnabled
                                        ? '연속으로 노쇼한 회원의 예약을 일정 기간 차단해요'
                                        : '연속 노쇼 제한이 꺼져있어요. 노쇼 횟수와 무관하게 계속 예약할 수 있어요'}
                                </span>
                            </Col>
                            <Toggle
                                value={form.consecutiveNoshowEnabled}
                                onChange={() => updateField('consecutiveNoshowEnabled', !form.consecutiveNoshowEnabled)}
                            />
                        </Row>
                        {form.consecutiveNoshowEnabled && (
                            <>
                                <NumberInput
                                    label="연속 노쇼 횟수"
                                    value={form.consecutiveNoshowLimit}
                                    onChange={(v) => updateField('consecutiveNoshowLimit', v)}
                                    suffix="회"
                                    min={1}
                                    description={
                                        <>연속으로 <b>{form.consecutiveNoshowLimit}회</b> 노쇼하면 예약이 제한돼요</>
                                    }
                                />
                                <NumberInput
                                    label="예약 금지 기간"
                                    value={form.consecutiveNoshowBanDays}
                                    onChange={(v) => updateField('consecutiveNoshowBanDays', v)}
                                    suffix="일"
                                    min={1}
                                    description={
                                        <>제한 발동 후 <b>{form.consecutiveNoshowBanDays}일간</b> 새 예약을 할 수 없어요</>
                                    }
                                />
                            </>
                        )}
                    </Section>

                    {/* ── 기타 ── */}
                    <Section title="기타 설정">
                        <Row style={{ alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                            <Col style={{ gap: 2 }}>
                                <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>당일 예약 제한</span>
                                <span style={{ fontSize: 12, color: '#6B7280' }}>
                                    {form.sameDayBookingEnabled
                                        ? '수업 당일, 설정한 시간 이후엔 예약을 받지 않아요'
                                        : '당일 예약 제한이 꺼져있어요. 자리가 남아 있으면 수업 시작 전까지 예약할 수 있어요'}
                                </span>
                            </Col>
                            <Toggle
                                value={form.sameDayBookingEnabled}
                                onChange={() => updateField('sameDayBookingEnabled', !form.sameDayBookingEnabled)}
                            />
                        </Row>
                        {form.sameDayBookingEnabled && (
                            <NumberInput
                                label="당일 예약 마감"
                                value={minsToHours(form.sameDayBookingDeadline)}
                                onChange={(v) => updateField('sameDayBookingDeadline', hoursToMins(v))}
                                suffix="시간 전"
                                description={
                                    <>수업 당일엔 수업 시작 <b>{formatNumber(minsToHours(form.sameDayBookingDeadline))}시간 전</b>까지만 예약을 받아요</>
                                }
                            />
                        )}
                        <Col style={{
                            marginTop: 8, padding: '12px 14px',
                            backgroundColor: '#F9FAFB', borderRadius: 8, border: '1px dashed #D1D5DB', gap: 6,
                        }}>
                            <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                                💡 <b>취소 횟수 제한</b>은 수강권 템플릿의 <b>취소 허용 횟수</b> · <b>당일 취소 허용 횟수</b>에서 설정해요.
                            </span>
                            {onNavigateToTickets && (
                                <button
                                    type="button"
                                    onClick={onNavigateToTickets}
                                    style={{
                                        alignSelf: 'flex-start',
                                        padding: '6px 12px', borderRadius: 6,
                                        fontSize: 12, fontWeight: 600,
                                        backgroundColor: 'white',
                                        border: '1px solid #BFDBFE', color: '#1D4ED8',
                                        cursor: 'pointer',
                                    }}>
                                    수강권 관리로 이동 →
                                </button>
                            )}
                        </Col>
                    </Section>
                </Col>
            </Col>
        </Col>
    );
};

const Section = ({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) => (
    <div style={{ padding: 24, backgroundColor: 'white', borderRadius: 12, border: '1px solid #E5E7EB' }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: '#111827', letterSpacing: '-0.01em' }}>{title}</span>
        {description && (
            <span style={{ display: 'block', fontSize: 13, color: '#6B7280', marginTop: 6, marginBottom: 16 }}>{description}</span>
        )}
        {!description && <div style={{ height: 16 }} />}
        <Col style={{ gap: 0 }}>
            {children}
        </Col>
    </div>
);

export default GroupSettingsPage;
